import { useState, useEffect } from 'react'
import { supabase } from '../../api/supabase'

type ReferenceCategory = 'ledger_account' | 'tax_rate' | 'analysis_category' | 'eu_goods_services_type'

interface ReferenceRow {
  id: string
  category: ReferenceCategory
  source_value: string
  sage_uuid: string | null
  description: string | null
  source: 'manual' | 'imported'
}

// Z68: 'analysis_category' is the DB/resolver name (matches ledger_account/tax_rate's style),
// but "Department" is what this actually means to Gary -- Sage's Analysis Type is literally
// named "Department" in his setup.
// Z67.18/Z67.20: 'eu_goods_services_type' -- an override for the value the CSV Bulk Import
// resolver always sends ("Services (standalone)") if Sage's real wording differs from its alias
// list. source_value is our normalized key ("Services"), not Sage's exact display name.
function categoryLabel(category: ReferenceCategory): string {
  if (category === 'analysis_category') return 'Department'
  if (category === 'ledger_account') return 'Ledger Account'
  if (category === 'eu_goods_services_type') return 'EU Goods/Services Type'
  return 'Tax Rate'
}

// Z67.16: mirrors withZeroPaddingVariants in supabase/functions/_shared/sage.ts -- the resolver
// already tries a numeric code both padded and unpadded, but auto-padding what Gary types here
// to Sage's own convention keeps this table's source_value consistent with what "Import Sage's
// Chart of Accounts" would write for the same code.
function zeroPadWidth(category: ReferenceCategory): number | null {
  if (category === 'analysis_category') return 2
  if (category === 'ledger_account') return 4
  return null
}
function autoPadSourceValue(category: ReferenceCategory, value: string): string {
  const width = zeroPadWidth(category)
  if (!width || !/^\d+$/.test(value)) return value
  return value.padStart(width, '0')
}

interface LiveItem {
  id: string
  display_name: string
}

function CopyableId({ value }: { value: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <button
      type="button"
      title="Copy to clipboard"
      onClick={() => {
        navigator.clipboard.writeText(value).then(() => {
          setCopied(true)
          setTimeout(() => setCopied(false), 1500)
        })
      }}
      className="font-mono text-xs text-left text-blue-700 dark:text-blue-400 hover:underline"
    >
      {copied ? 'Copied ✓' : value}
    </button>
  )
}

// Z65 addenda / Z67.3: Ledger Account (e.g. '4002') and VAT Rate (e.g. 'Standard') -- the EXACT
// strings BoxTop's CSV export uses -- map to Sage's own UUID, for the CSV bulk import (see
// SageBulkImport.tsx). "Show Sage's list" calls the same live GET /ledger_accounts /
// GET /tax_rates Gary would otherwise have to look up in Sage's own UI, returned here as just
// {id, display_name} pairs so the UUID (not Sage's raw response shape) can be copied straight
// into the "Sage UUID" field below.
export default function SageReferenceData() {
  const [rows, setRows] = useState<ReferenceRow[]>([])
  const [loading, setLoading] = useState(true)
  const [form, setForm] = useState<{ category: ReferenceCategory; source_value: string; sage_uuid: string; description: string }>({
    category: 'ledger_account', source_value: '', sage_uuid: '', description: '',
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [liveList, setLiveList] = useState<{ label: string; items: LiveItem[] } | null>(null)
  const [loadingLive, setLoadingLive] = useState<ReferenceCategory | null>(null)
  const [importing, setImporting] = useState(false)
  const [importMsg, setImportMsg] = useState<string | null>(null)
  // Z67.17: inline row editing -- Category and Source (manual/imported) stay readonly (editing
  // Category would change the row's identity under the category+source_value unique key, and
  // Source reflects HOW the row was created, not something Gary should be able to fake).
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editForm, setEditForm] = useState<{ source_value: string; sage_uuid: string; description: string }>({
    source_value: '', sage_uuid: '', description: '',
  })
  const [savingEdit, setSavingEdit] = useState(false)
  // Z67.19: "Inspect a Sales Invoice" diagnostic -- pulls a Sage invoice's RAW JSON (ours, or one
  // Ada created by hand in Sage's own UI) so the two can be compared to see exactly how Sage
  // expects Department (analysis_type_line_items) to look, without direct API/Postman access.
  const [inspectInvoiceId, setInspectInvoiceId] = useState('')
  const [inspecting, setInspecting] = useState(false)
  const [inspectResult, setInspectResult] = useState<{ ok: true; invoice: any } | { ok: false; error: string } | null>(null)

  const fetchRows = async () => {
    setLoading(true)
    const { data } = await supabase.from('sage_reference_data').select('*').order('category').order('source_value')
    setRows(data || [])
    setLoading(false)
  }

  useEffect(() => { fetchRows() }, [])

  const handleSave = async () => {
    if (!form.source_value.trim() || !form.sage_uuid.trim()) return
    setSaving(true); setError(null)
    try {
      const { error: err } = await supabase.from('sage_reference_data').upsert({
        category: form.category,
        source_value: autoPadSourceValue(form.category, form.source_value.trim()),
        sage_uuid: form.sage_uuid.trim(),
        description: form.description.trim() || null,
        source: 'manual',
      }, { onConflict: 'category,source_value' })
      if (err) throw err
      setForm({ category: form.category, source_value: '', sage_uuid: '', description: '' })
      await fetchRows()
    } catch (err: any) {
      setError(err?.message || 'Failed to save')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id: string) => {
    await supabase.from('sage_reference_data').delete().eq('id', id)
    fetchRows()
  }

  const startEdit = (row: ReferenceRow) => {
    setEditingId(row.id)
    setEditForm({ source_value: row.source_value, sage_uuid: row.sage_uuid || '', description: row.description || '' })
    setError(null)
  }

  const cancelEdit = () => {
    setEditingId(null)
    setEditForm({ source_value: '', sage_uuid: '', description: '' })
  }

  const saveEdit = async (row: ReferenceRow) => {
    if (!editForm.source_value.trim()) return
    setSavingEdit(true); setError(null)
    try {
      const { error: err } = await supabase.from('sage_reference_data').update({
        source_value: autoPadSourceValue(row.category, editForm.source_value.trim()),
        sage_uuid: editForm.sage_uuid.trim() || null,
        description: editForm.description.trim() || null,
        // Z67.17: source is deliberately NOT touched here -- editing values shouldn't turn an
        // 'imported' snapshot row into a 'manual' override, or vice versa.
      }).eq('id', row.id)
      if (err) throw err
      cancelEdit()
      await fetchRows()
    } catch (err: any) {
      setError(err?.message || 'Failed to save')
    } finally {
      setSavingEdit(false)
    }
  }

  const showLiveList = async (category: ReferenceCategory) => {
    setLoadingLive(category)
    setLiveList(null)
    try {
      const { data: sessionData } = await supabase.auth.getSession()
      const accessToken = sessionData?.session?.access_token || ''
      const action = category === 'ledger_account' ? 'inspect_ledger_accounts'
        : category === 'tax_rate' ? 'inspect_tax_rates'
        : category === 'eu_goods_services_type' ? 'inspect_eu_goods_services_types'
        : 'list_department_categories'
      const resp = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sage-sync-invoice`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({ action }),
      })
      const result = await resp.json()
      if (!result.success) {
        setError(result.error || 'Failed to load from Sage')
        return
      }
      // Z68: list_department_categories returns {code, id, name} instead of {id, display_name}
      // -- reshape to the same LiveItem shape everything else here already renders.
      const items: LiveItem[] = category === 'analysis_category'
        ? (Array.isArray(result.items) ? result.items : []).map((it: any) => ({ id: it.id, display_name: `${it.code} — ${it.name}` }))
        : (Array.isArray(result.items) ? result.items : [])
      setLiveList({ label: category === 'analysis_category' ? 'Department categories' : `${categoryLabel(category)}s`, items })
    } catch (err: any) {
      setError(err?.message || 'Failed to load from Sage')
    } finally {
      setLoadingLive(null)
    }
  }

  const handleImportChart = async () => {
    setImporting(true); setImportMsg(null); setError(null)
    try {
      const { data: sessionData } = await supabase.auth.getSession()
      const accessToken = sessionData?.session?.access_token || ''
      const resp = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sage-sync-invoice`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({ action: 'import_chart_of_accounts' }),
      })
      const result = await resp.json()
      if (!result.success) {
        setError(result.error || 'Failed to import from Sage')
        return
      }
      setImportMsg(`Imported ${result.imported} row(s) for visibility (manual overrides were left untouched).`)
      await fetchRows()
    } catch (err: any) {
      setError(err?.message || 'Failed to import from Sage')
    } finally {
      setImporting(false)
    }
  }

  const handleInspectInvoice = async () => {
    if (!inspectInvoiceId.trim()) return
    setInspecting(true); setInspectResult(null)
    try {
      const { data: sessionData } = await supabase.auth.getSession()
      const accessToken = sessionData?.session?.access_token || ''
      const resp = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sage-sync-invoice`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({ action: 'inspect_sales_invoice', sage_invoice_id: inspectInvoiceId.trim() }),
      })
      const result = await resp.json()
      setInspectResult(result.success ? { ok: true, invoice: result.invoice } : { ok: false, error: result.error || 'Failed to load invoice from Sage' })
    } catch (err: any) {
      setInspectResult({ ok: false, error: err?.message || 'Failed to load invoice from Sage' })
    } finally {
      setInspecting(false)
    }
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white">Sage Reference Data</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Bulk Import resolves Ledger Account / VAT Rate values straight from Sage's live chart of accounts
          -- no manual mapping needed for that to work, and a new Sage ledger account is picked up
          automatically on the next import. Add a row below only to force a specific <span className="font-semibold">override</span> that
          differs from what Sage would otherwise resolve.
        </p>
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
          Codes are matched with leading-zero normalization (e.g. "2" matches "02") -- no need to worry about which spelling the CSV or Sage uses.
        </p>
      </div>

      <div className="flex gap-2 mb-6 flex-wrap">
        <button onClick={() => showLiveList('ledger_account')} disabled={loadingLive !== null}
          className="px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-white rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-sm font-medium disabled:opacity-50">
          {loadingLive === 'ledger_account' ? 'Loading…' : "Show Sage's Ledger Accounts"}
        </button>
        <button onClick={() => showLiveList('tax_rate')} disabled={loadingLive !== null}
          className="px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-white rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-sm font-medium disabled:opacity-50">
          {loadingLive === 'tax_rate' ? 'Loading…' : "Show Sage's Tax Rates"}
        </button>
        <button onClick={() => showLiveList('analysis_category')} disabled={loadingLive !== null}
          className="px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-white rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-sm font-medium disabled:opacity-50">
          {loadingLive === 'analysis_category' ? 'Loading…' : "Show Sage's Department Categories"}
        </button>
        <button onClick={() => showLiveList('eu_goods_services_type')} disabled={loadingLive !== null}
          className="px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-white rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-sm font-medium disabled:opacity-50">
          {loadingLive === 'eu_goods_services_type' ? 'Loading…' : "Show Sage's EU Goods/Services Types"}
        </button>
        <button onClick={handleImportChart} disabled={importing}
          className="px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-white rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-sm font-medium disabled:opacity-50">
          {importing ? 'Importing…' : "Import Sage's Chart of Accounts to our Reference Data"}
        </button>
      </div>

      {importMsg && <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 rounded-lg text-sm">{importMsg}</div>}

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-4 mb-6">
        <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Inspect a Sales Invoice</p>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
          Paste a Sage invoice's id (ours, from sage_csv_import_rows.sage_invoice_id, or one Ada created by hand in
          Sage's own UI) to see its raw JSON -- e.g. to compare how Department (analysis_type_line_items) actually
          looks on a working invoice versus one of ours.
        </p>
        <div className="flex gap-2">
          <input
            value={inspectInvoiceId}
            onChange={e => setInspectInvoiceId(e.target.value)}
            placeholder="Sage invoice UUID"
            className="flex-1 text-sm font-mono border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md px-2 py-1.5"
          />
          <button onClick={handleInspectInvoice} disabled={inspecting || !inspectInvoiceId.trim()}
            className="px-4 py-1.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-white rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-sm font-medium disabled:opacity-50">
            {inspecting ? 'Loading…' : 'Inspect'}
          </button>
        </div>
        {inspectResult && (
          inspectResult.ok ? (
            <pre className="mt-3 max-h-72 overflow-auto text-xs bg-gray-50 dark:bg-gray-900 rounded-lg p-3 text-gray-700 dark:text-gray-300">
              {JSON.stringify(inspectResult.invoice, null, 2)}
            </pre>
          ) : (
            <p className="mt-3 text-sm text-red-600 dark:text-red-400">{inspectResult.error}</p>
          )
        )}
      </div>

      {liveList && (
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 mb-6 text-sm">
          <p className="font-semibold text-blue-800 dark:text-blue-300 mb-2">Sage's live {liveList.label} ({liveList.items.length})</p>
          {liveList.items.length === 0 ? (
            <p className="text-blue-700 dark:text-blue-400">None returned -- check Sage is connected (Admin &gt; Connect Sage).</p>
          ) : (
            <div className="max-h-56 overflow-y-auto rounded-lg border border-blue-200 dark:border-blue-800">
              <table className="w-full text-xs">
                <thead className="bg-blue-100 dark:bg-blue-900/40 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold text-blue-800 dark:text-blue-300">Name</th>
                    <th className="px-3 py-2 text-left font-semibold text-blue-800 dark:text-blue-300">Sage UUID (click to copy)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-blue-100 dark:divide-blue-900">
                  {liveList.items.map(item => (
                    <tr key={item.id} className="hover:bg-blue-100/50 dark:hover:bg-blue-900/30">
                      <td className="px-3 py-1.5 text-blue-900 dark:text-blue-200">{item.display_name}</td>
                      <td className="px-3 py-1.5"><CopyableId value={item.id} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {error && <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg text-sm">{error}</div>}

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-4 mb-6">
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 items-end">
          <label className="block">
            <span className="text-xs text-gray-600 dark:text-gray-400">Category</span>
            <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value as any }))}
              className="mt-1 w-full text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md px-2 py-1.5">
              <option value="ledger_account">Ledger Account</option>
              <option value="tax_rate">Tax Rate</option>
              <option value="analysis_category">Department</option>
              <option value="eu_goods_services_type">EU Goods/Services Type</option>
            </select>
          </label>
          <label className="block">
            <span className="text-xs text-gray-600 dark:text-gray-400">Source value (from BoxTop CSV, e.g. Standard, Exempt, 4002, or a Department code like 02)</span>
            <input value={form.source_value} onChange={e => setForm(f => ({ ...f, source_value: e.target.value }))}
              className="mt-1 w-full text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md px-2 py-1.5" />
          </label>
          <label className="block">
            <span className="text-xs text-gray-600 dark:text-gray-400">Sage UUID (copy from the list above)</span>
            <input value={form.sage_uuid} onChange={e => setForm(f => ({ ...f, sage_uuid: e.target.value }))}
              className="mt-1 w-full text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md px-2 py-1.5" />
          </label>
          <label className="block">
            <span className="text-xs text-gray-600 dark:text-gray-400">Description</span>
            <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              className="mt-1 w-full text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md px-2 py-1.5" />
          </label>
          <button onClick={handleSave} disabled={saving} className="px-4 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50">
            {saving ? 'Saving…' : 'Add / Update'}
          </button>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-700 border-b dark:border-gray-600">
              <tr>
                {['Category', 'Source value', 'Sage UUID', 'Description', 'Source', ''].map(h => (
                  <th key={h} className="px-4 py-3 text-left font-semibold text-gray-600 dark:text-gray-300">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {rows.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-400">No mappings yet</td></tr>
              ) : rows.map(r => {
                const isEditing = editingId === r.id
                return (
                  <tr key={r.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{categoryLabel(r.category)}</td>
                    <td className="px-4 py-3 font-mono font-semibold text-gray-800 dark:text-gray-200">
                      {isEditing ? (
                        <input value={editForm.source_value} onChange={e => setEditForm(f => ({ ...f, source_value: e.target.value }))}
                          className="w-full text-sm font-mono border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md px-2 py-1" />
                      ) : r.source_value}
                    </td>
                    <td className="px-4 py-3 font-mono text-gray-700 dark:text-gray-300">
                      {isEditing ? (
                        <input value={editForm.sage_uuid} onChange={e => setEditForm(f => ({ ...f, sage_uuid: e.target.value }))}
                          placeholder="Sage UUID"
                          className="w-full text-sm font-mono border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md px-2 py-1" />
                      ) : r.sage_uuid ? <CopyableId value={r.sage_uuid} /> : <span className="text-amber-600 dark:text-amber-400">not mapped yet</span>}
                    </td>
                    <td className="px-4 py-3 text-gray-500 dark:text-gray-400">
                      {isEditing ? (
                        <input value={editForm.description} onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))}
                          className="w-full text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md px-2 py-1" />
                      ) : r.description || '—'}
                    </td>
                    <td className="px-4 py-3">
                      {r.source === 'manual' ? (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300">override</span>
                      ) : (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">imported</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {isEditing ? (
                        <div className="flex gap-2">
                          <button onClick={() => saveEdit(r)} disabled={savingEdit} className="text-xs text-blue-600 hover:underline disabled:opacity-50">
                            {savingEdit ? 'Saving…' : 'Save'}
                          </button>
                          <button onClick={cancelEdit} disabled={savingEdit} className="text-xs text-gray-500 hover:underline disabled:opacity-50">Cancel</button>
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          <button onClick={() => startEdit(r)} className="text-xs text-blue-600 hover:underline">Edit</button>
                          <button onClick={() => handleDelete(r.id)} className="text-xs text-red-600 hover:underline">Delete</button>
                        </div>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
